import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import userDetail from "Stores/userDetail";
import { tagsStoreData } from "Stores/TagsData";
import { settingsStoreData } from "Stores/settingsData";
import contactStoreData from "Stores/contactData";
import Utils from "Utils/utils";
import { DateUtils } from "Utils/DateUtils";
import MultiSelectDropdown from "Pages/Dashboard/contacts/ContactFilter/MultiSelectDropdown";
import ContactFieldFilter from "Pages/Dashboard/contacts/ContactFilter/ContactFieldFilter";
import ContactMetricsFilter from "Pages/Dashboard/contacts/ContactFilter/ContactMetricsFilter";
import ContactDateFilter from "Pages/Dashboard/contacts/ContactFilter/ContactDateFilter";
import ContactSequenceFilter from "Pages/Dashboard/contacts/ContactFilter/ContactSequenceFilter";
import {
  contactMetricFilters,
  websiteFilters,
  linkedInFilters,
  whatsappFilters
} from "Pages/Dashboard/contacts/ContactFilter/FilterConstants";

const getDefaultObj = () => ({
  tags: tagsStoreData?.tagsData || [],
  owners: userDetail?.userInfo?.hasTeamMember || [],
  contactFields: [{ fieldName: "", operator: "EQUALS", value: "" }],
  metrics: [{ fieldName: "", operator: "EQUALS", value: "" }],
  dates: [{ fieldName: "" }],
  sequences: [{ fieldName: "", sequenceId: "", sequenceName: "" }]
});

const AccountContactFilter = observer((props = {}) => {
  const {
    accountId = "",
    searchTerm = "",
    stageId = "",
    limit = 10,
    onApply = () => {},
    onClear = () => {},
    onCancel = () => {}
  } = props || {};

  const { hasTeamMember } = userDetail?.userInfo || {};

  const [data, setData] = useState(getDefaultObj());
  const [filterArr, setFilterArr] = useState([]);
  const [activeFilter, setActiveFilter] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [sequenceLoader, setSequenceLoader] = useState(true);
  const [disableContactFieldAddBtn, setDisableContactFieldAddBtn] = useState(true);

  // ── Data handlers ────────────────────────────────────────────────────────

  const handleFilterObject = (key = "", value = "") => {
    let tempObj = { ...data, [key]: value };
    setData(tempObj);
    if (key === "contactFields") {
      const last = tempObj[key]?.[tempObj[key].length - 1];
      const valid = last?.fieldName && (["IS_SET", "IS_NOT_SET"].includes(last?.operator) || last?.value);
      setDisableContactFieldAddBtn(!valid);
    }
  };

  const handleActiveFilter = (value = "") => {
    setActiveFilter(value === activeFilter ? "" : value);
  };

  const selectedFilterAction = (type = "", value = "") => {
    let tempArr = [...filterArr];
    if (type === "remove") {
      tempArr = tempArr.filter(item => item !== value);
    } else if (type === "add" && !tempArr.includes(value)) {
      tempArr.push(value);
    }
    setFilterArr(tempArr);
  };

  const getSelectedItems = (item = []) => {
    return item?.length ? item.filter(i => i?.checked) : [];
  };

  // ── Extract filter values ────────────────────────────────────────────────

  const getTagIds = () => {
    let ids = [];
    if (filterArr.includes("tags") && data.tags?.length) {
      data.tags.forEach(t => { if (t?.checked) ids.push(t.id); });
    }
    return ids;
  };

  const getMemberIds = () => {
    let ids = [];
    if (filterArr.includes("owners") && data.owners?.length) {
      data.owners.forEach(o => { if (o?.checked && !ids.includes(o?.memberId)) ids.push(o.memberId); });
    }
    return ids;
  };

  const getContactFields = () => {
    if (filterArr.includes("contactFields") && data.contactFields?.length) {
      return data.contactFields.filter(i => i?.fieldName && i?.operator);
    }
    return [];
  };

  const mapObj = {
    hasvisitedwebsite: "websiteVisitedCount",
    profileviewscount: "profileViewsCount",
    linkedinconnectionrequest: { sent: "liCrSentCount", accepted: "liCrAcceptedCount" },
    linkedinmessage: { sent: "liMessageSentCount", replied: "liMessageReplyCount" }
  };

  const mapMetricsItem = item => {
    if (item?.fieldName && item?.operator) {
      const key = item.fieldName.toLowerCase();
      if (["hasvisitedwebsite", "profileviewscount"].includes(key)) {
        return { ...item, value: 0, operator: item.value === "true" ? "GREATER_THAN" : "EQUALS", fieldName: mapObj[key] };
      }
      if (["linkedinconnectionrequest", "linkedinmessage"].includes(key)) {
        return { ...item, value: 0, operator: "GREATER_THAN", fieldName: mapObj[key][item.value?.toLowerCase()] };
      }
      return item;
    }
  };

  const getMetrics = () => {
    if (filterArr.includes("metrics") && data.metrics?.length) {
      return data.metrics.filter(i => i?.fieldName && i?.operator).map(mapMetricsItem);
    }
    return [];
  };

  const getDates = () => {
    if (filterArr.includes("dates") && data.dates?.length) {
      return DateUtils.getDateFilterObj(data.dates);
    }
    return "";
  };

  const getSequencesV2 = () => {
    if (filterArr.includes("sequences") && data.sequences?.length) {
      let obj = { addedMultiSequences: [], completedMultiSequences: [], activeMultiSequences: [] };
      data.sequences.forEach(item => {
        const { fieldName = "", sequenceId = "", operator = "EQUALS" } = item || {};
        if (fieldName && sequenceId) {
          if (fieldName === "ADDED_TO_SEQUENCE") obj.addedMultiSequences.push({ sequenceId, operator });
          else if (fieldName === "COMPLETED_IN_SEQUENCE") obj.completedMultiSequences.push({ sequenceId, operator });
          else if (fieldName === "ACTIVE_IN_SEQUENCE") obj.activeMultiSequences.push({ sequenceId, operator });
        }
      });
      Object.keys(obj).forEach(k => { if (!obj[k].length) delete obj[k]; });
      return obj;
    }
    return "";
  };

  // ── Validation ───────────────────────────────────────────────────────────

  const validateSelected = (showErr = true) => {
    const { dates = [], sequences = [], contactFields = [] } = data || {};
    for (const obj of contactFields) {
      if (obj?.fieldName && obj?.operator && !obj?.value && !["IS_SET", "IS_NOT_SET"].includes(obj?.operator)) {
        if (showErr) { setActiveFilter("contactFields"); setErrMsg("contactFields"); }
        return false;
      }
    }
    for (const obj of dates) {
      if (obj?.fieldName && obj?.operator === "BETWEEN" && (!obj?.fromDate || !obj?.toDate)) {
        if (showErr) { setActiveFilter("dates"); setErrMsg("dates"); }
        return false;
      }
    }
    for (const obj of sequences) {
      if (obj?.fieldName && !obj?.sequenceId) {
        if (showErr) { setActiveFilter("sequences"); setErrMsg("sequences"); }
        return false;
      }
    }
    showErr && setErrMsg("");
    return true;
  };

  // ── Apply / Clear ────────────────────────────────────────────────────────

  const applyFilter = () => {
    if (!validateSelected()) return;
    const filter = {
      accountId: [accountId],
      tagIds: getTagIds(),
      memberIds: getMemberIds()
    };
    let fields = getContactFields();
    let metrics = getMetrics();
    if (metrics?.length) fields = fields.concat(metrics);
    if (fields?.length) filter.fields = fields;
    let dates = getDates();
    if (dates && Object.keys(dates)?.length) Object.assign(filter, dates);
    let sequences = getSequencesV2();
    if (sequences && Object.keys(sequences)?.length) Object.assign(filter, sequences);

    onApply(filter, filterArr.length);
  };

  const clearAllFilter = () => {
    setActiveFilter("");
    setFilterArr([]);
    // Reset tags
    if (data.tags?.length) {
      let tags = data.tags.map(t => ({ ...t, checked: false }));
      tagsStoreData.setTagsData(tags);
    }
    // Reset owners
    if (data.owners?.length) {
      let owners = data.owners.map(o => ({ ...o, checked: false }));
      userDetail.setUserInfo({ ...userDetail.userInfo, hasTeamMember: owners });
    }
    setData(getDefaultObj());
    onClear();
  };

  const cancelFilter = () => {
    clearAllFilter();
    onCancel();
  };

  // ── Sequence list ────────────────────────────────────────────────────────

  const getSequenceList = (pageNo = "") => {
    const payload = {
      pageNo: pageNo || contactStoreData?.sequenceCurrentPageNo,
      limit: 500,
      filters: { memberIds: [userDetail?.userInfo?.memberId] }
    };
    contactStoreData.listSequences(payload);
  };

  const handleScrollEnd = event => {
    const { scrollHeight = 0, scrollTop = 0, clientHeight = 0 } = event?.currentTarget;
    if (scrollHeight - scrollTop - 10 < clientHeight &&
        contactStoreData.sequenceCurrentPageNo < contactStoreData.sequenceTotalPages) {
      const newPageNumber = contactStoreData.sequenceCurrentPageNo + 1;
      contactStoreData.setSequenceCurrentPageNo(newPageNumber);
      getSequenceList(newPageNumber);
    }
  };

  // ── Init ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    tagsStoreData.getAllTags();
    settingsStoreData.getCustomFields();
    getSequenceList();
  }, []);

  useEffect(() => {
    handleFilterObject("tags", tagsStoreData.tagsData);
  }, [tagsStoreData.tagsData]);

  useEffect(() => {
    handleFilterObject("owners", userDetail.userInfo.hasTeamMember);
  }, [userDetail.userInfo.hasTeamMember]);

  useEffect(() => {
    setSequenceLoader(false);
  }, [contactStoreData?.sequenceList]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="contactFilterByOption">
      <div className="filterHeader">
        <span className="name">Filter</span>
        <span
          className={`clearBtn ${filterArr?.length === 0 ? "disabled" : ""}`}
          onClick={clearAllFilter}
        >
          Clear All
        </span>
      </div>
      <div className="filterBody">
        <MultiSelectDropdown
          label="Tags"
          keyValue="tags"
          placeholder="Search by tags"
          selected={getSelectedItems(data?.tags)}
          data={data?.tags}
          setAction={handleFilterObject}
          activeFilter={activeFilter}
          setActiveFilter={handleActiveFilter}
          selectedFilterCbk={selectedFilterAction}
        />
        <MultiSelectDropdown
          label="Owners"
          keyValue="owners"
          placeholder="Search by owners"
          selected={getSelectedItems(data?.owners)}
          data={data?.owners}
          setAction={handleFilterObject}
          activeFilter={activeFilter}
          setActiveFilter={handleActiveFilter}
          selectedFilterCbk={selectedFilterAction}
          showEmptyText={true}
        />
        <ContactFieldFilter
          label="Contact Field"
          keyValue="contactFields"
          data={data?.contactFields}
          activeFilter={activeFilter}
          setActiveFilter={handleActiveFilter}
          setAction={handleFilterObject}
          selectedFilterCbk={selectedFilterAction}
          errMsg={errMsg}
          setErrMsg={setErrMsg}
          disableAddButton={disableContactFieldAddBtn}
        />
        <ContactMetricsFilter
          label="Metrics"
          keyValue="metrics"
          data={data?.metrics}
          activeFilter={activeFilter}
          setActiveFilter={handleActiveFilter}
          setAction={handleFilterObject}
          selectedFilterCbk={selectedFilterAction}
          errMsg={errMsg}
          setErrMsg={setErrMsg}
          pageType="contacts"
          customConditionFieldnameList={[
            ...contactMetricFilters,
            ...websiteFilters,
            ...linkedInFilters,
            ...whatsappFilters
          ]}
        />
        <ContactDateFilter
          label="Date"
          keyValue="dates"
          data={data?.dates}
          activeFilter={activeFilter}
          setActiveFilter={handleActiveFilter}
          setAction={handleFilterObject}
          selectedFilterCbk={selectedFilterAction}
          errMsg={errMsg}
          setErrMsg={setErrMsg}
        />
        <ContactSequenceFilter
          label="Sequence"
          keyValue="sequences"
          data={data?.sequences}
          sequenceLoader={sequenceLoader}
          sequenceList={contactStoreData?.sequenceList}
          activeFilter={activeFilter}
          setActiveFilter={handleActiveFilter}
          setAction={handleFilterObject}
          selectedFilterCbk={selectedFilterAction}
          handleScrollEnd={handleScrollEnd}
          errMsg={errMsg}
          setErrMsg={setErrMsg}
          filterFn={Utils.getFilterFn("FILTER_SEQUENCE")}
        />
      </div>
      <div className="filterAction">
        <div className="cancelFilter" onClick={cancelFilter}>Cancel</div>
        <div className="applyFilter" onClick={applyFilter}>Apply Filter</div>
      </div>
    </div>
  );
});

export default AccountContactFilter;
